import React, { useEffect, useState } from "react";
import { useIntl } from "react-intl";
import { useSelector, useDispatch } from "react-redux";
import moment from "moment";

//Controls
import { NotificationManager } from "Components/Notifications";
import {
    TextArea,
    MenuComponent,
    TableButtons,
    Button,
    Input,
    IconButton
} from "@gpstrackit/gtc-react-controls";
import { Grid } from "@mui/material";
import { Timezone } from 'Components/uiControls';
import useExport from "Components/Table/Export";

//Slice
import {
    workOrdersSettingsSearchComments,
    workOrdersSettingsCreateComment,
    workOrdersSettingsDeleteComment,
    workOrdersSettingsUpdateComment,
    resetWorkOrderComments
} from './redux/slice';

//Helpers
import { GetFormatForMoment, splitTextIntoLines } from 'Core/data/Helpers';

//Styles
import './index.scss'

const EditComment = (props) => {

    const { comment } = props;
    const { messages } = useIntl();

    const dispatch = useDispatch();

    //Redux States
    const { loading, update } = useSelector((state) => state.workOrdersCommentsWorkOrderRedux);
    const user = useSelector((state) => state.securityRedux.user);
    const { dateformat, timeZone } = user;



    //Local States
    const [isEditable, setIsEditable] = useState(false);
    const [value, setValue] = useState("");

    useEffect(() => {
        if (update) {
            setIsEditable(false)
            setValue("");
        }
    }, [update])

    const getCommentsOptions = () => [
        messages["edit"],
        messages["delete"]
    ];

    return (
        <div>
            <Grid
                container
                direction="row"
                sx={{
                    justifyContent: "space-between",
                    alignItems: "center",
                }}
            >
                <Grid item xs={10}>
                    <span style={{ fontWeight: 700 }}>{comment?.nameCreatedBy?.name}</span>
                </Grid>
                {
                    !isEditable ? (
                        <>
                            <Grid item xs={1} style={{ textAlign: 'end' }}>
                                <MenuComponent
                                    placement="right-start"
                                    options={getCommentsOptions()}
                                    icon="more_vert"
                                    className="showInHover"
                                    onClick={(type) => {
                                        let currentUser = user?.subUserId ? `PX_${user?.companyId}_${user?.subUserId}` : `PX_${user?.companyId}`
                                        if (currentUser !== comment?.createdBy) {
                                            NotificationManager.error(
                                                messages['workOrder_comments_errorUpdateComment'],
                                                messages["error"],
                                                3000,
                                            );
                                            return
                                        } else if (type === messages["delete"]) {
                                            dispatch(workOrdersSettingsDeleteComment({ id: comment?.id }))
                                        } else if (type === messages["edit"]) {
                                            setValue(comment?.comment);
                                            setIsEditable(true);
                                        }
                                    }}
                                    value={comment}
                                />
                            </Grid>
                            <Grid item xs={12}>
                                <Timezone
                                    date={comment?.updatedOn}
                                    dateFormat={dateformat}
                                    timeFormat="hh:mm a"
                                    timezone={timeZone}
                                />
                                <div style={{ wordWrap: 'break-word', width: 365 }}>
                                    <span>{comment?.comment}</span>
                                </div>
                            </Grid>
                        </>
                    ) : (
                        <>
                            <Grid item xs={12} style={{ marginTop: 10 }}>
                                <TextArea
                                    id="editable-comment"
                                    value={value}
                                    onChange={(value) => setValue(value)}
                                    maxLength={500}
                                />
                            </Grid>
                            <Grid item xs={12}>
                                <Button
                                    isDisabled={loading}
                                    style={{ marginRight: 8 }}
                                    onClick={() => {
                                        setIsEditable(false);
                                        setValue("");
                                    }}
                                    variant="secondary"
                                >
                                    {messages["cancel"]}
                                </Button>
                                <Button
                                    isDisabled={loading || !value.length}
                                    onClick={() => {
                                        dispatch(workOrdersSettingsUpdateComment({
                                            id: comment?.id,
                                            jsonDetails: JSON.stringify({ comment: value, action: "updateComment" })
                                        }))
                                    }}
                                    variant="primary"
                                >
                                    {messages["save"]}
                                </Button>
                            </Grid>
                        </>
                    )
                }
            </Grid>
        </div>
    )
}
const Comments = (props) => {

    const { messages } = useIntl();
    const dispatch = useDispatch();

    //Local States
    const [comment, setComment] = useState(null)

    //Redux States
    const { open, workOrderId } = useSelector((state) => state.workOrdersCreateEditWorkOrderRedux);
    const { comments, loading, create, deleteComment, error, update } = useSelector((state) => state.workOrdersCommentsWorkOrderRedux);
    const user = useSelector((state) => state.securityRedux.user);
    const { dateformat } = user;

    const format = GetFormatForMoment(dateformat);

    useEffect(() => {
        if (open) {
            dispatch(workOrdersSettingsSearchComments({ workOrderId: workOrderId }))
        }
    }, [open])

    useEffect(() => {
        if (create || update) {
            NotificationManager.success(
                messages['workOrder_comments_createSuccessMessages'],
                messages["success"],
                3000
            );
            setComment("")
            dispatch(workOrdersSettingsSearchComments({ workOrderId: workOrderId }))
            dispatch(resetWorkOrderComments({ create: null, update: null }))
        }
    }, [create, update])

    useEffect(() => {
        if (deleteComment) {
            NotificationManager.success(
                messages['workOrder_comments_deleteSuccessMessages'],
                messages["success"],
                3000
            );
            dispatch(workOrdersSettingsSearchComments({ workOrderId: workOrderId }))
            dispatch(resetWorkOrderComments({ deleteComment: null }))
        }
    }, [deleteComment])

    useEffect(() => {
        if (error) {
            NotificationManager.error(
                messages['errorMessage'],
                messages["error"],
                3000,
            );
            dispatch(resetWorkOrderComments({ error: null }))
        }
    }, [error])

    const handleCreateComments = () => {
        let payload = {
            workOrderId: workOrderId,
            logType: "comments",
            jsonDetails: JSON.stringify({ comment: comment, action: "createComment" })
        }
        dispatch(workOrdersSettingsCreateComment(payload))
    }

    const getHeaders = () => {
        return [
            messages['workOrder_comments_createBy'],
            messages['workOrder_comments_createOn'],
            messages['workOrder_comments_comment']
        ];
    };

    const getDataToExport = (rows) => {
        let data = [];
        rows.forEach((row) => {
            let jsonDetails = ''
            try {
                jsonDetails = JSON.parse(row?.jsonDetails)
            } catch (error) { }
            let updatedOn = row?.updatedOn ? moment(row?.updatedOn).format(`${format} hh:mm a`) : ""
            data.push([row?.nameCreatedBy?.name, updatedOn, jsonDetails?.comment ? splitTextIntoLines(jsonDetails?.comment, 100) : ""]);
        });
        return data;
    };

    const exportCSVPdf = useExport(
        getHeaders,
        getDataToExport,
        [],
        comments || [],
        null,
        `work-order-comments-${moment().unix()}`,
        "landscape",
        true
    );

    const onExport = (type) => {
        if (type === "Csv") {
            exportCSVPdf.exportCSV();
        } else if (type === "Pdf") {
            exportCSVPdf.exportPDF();
        }
    }

    return (
        <div className="work-orders-comments">
            <div style={{ display: 'flex', width: '100%', justifyContent: 'flex-end', height: 34 }}>
                {
                    comments.length ? (
                        <>
                            <TableButtons
                                messages={messages}
                                buttons={["export"]}
                                onExport={(type) => onExport(type)}
                            />
                            <IconButton
                                icon="refresh"
                                tooltip={messages["table_refresh"]}
                                size="small"
                                placement="bottom"
                                style={{ color: 'black' }}
                                onClick={() => {
                                    dispatch(workOrdersSettingsSearchComments({ workOrderId: workOrderId }))
                                }}
                            />
                        </>

                    ) : <></>
                }
            </div>
            <div className="work-orders-comments-container">
                {
                    comments?.length ? comments?.map((item) => {
                        let data;
                        try {
                            data = JSON.parse(item?.jsonDetails);
                        } catch (error) {
                            data = {};
                        }
                        return (
                            <div key={item?.id} className="comments-item">
                                <EditComment comment={{ ...item, comment: data?.comment }} />
                            </div>
                        )
                    }) : <div style={{ marginTop: 15, textAlign: 'center', opacity: 0.5 }}> {messages['no_data']}</div>
                }
            </div>
            <div>
                <Grid
                    container
                    direction="row"
                    sx={{
                        justifyContent: "space-between",
                        alignItems: "center",
                    }}
                    style={{ marginTop: 5 }}
                >
                    <Grid item xs={9}>
                        <Input
                            id="comment"
                            type="text"
                            placeholder={messages['workOrder_comments_placeholder']}
                            onChange={(value) => setComment(value)}
                            maxLength={500}
                            value={comment}
                        />
                    </Grid>
                    <Grid item xs={3}>
                        <Button
                            isDisabled={loading || !comment?.length}
                            onClick={() => handleCreateComments()}
                            variant="primary"
                            style={{ height: 40, width: "100%", marginLeft: 5 }}
                        >
                            {messages['workOrder_comments_send']}
                        </Button>
                    </Grid>
                </Grid>
            </div>
        </div>
    )
}


export default Comments